/**
 * 1.4.0 asset groups, stream G2: an asset's groups where the asset is shown (step 0 mounted each; G2 fills them).
 *
 *  - AssetGroupsBreadcrumb: the asset page's header, "Series › Season 1 – Murder House · Episode 3" (Asset.tsx; on a
 *    phone only this).
 *  - AssetGroupsRow: a "Groups" row with Change and Remove (group.manage): the Library's Inspector
 *    (LibraryInspector.tsx) and the asset page's Metadata tab (MetadataTab.tsx).
 *  - InheritedFieldValue: under a metadata field whose own value is empty, the inherited value in grey with
 *    "from Series" (MetadataSections' renderAfterField); typing a value makes it the asset's own.
 *
 * Data: AssetSummary.groups and .inheritedMetadata (G1's lookup). G2's file.
 */
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderTree, Pencil, X } from 'lucide-react';
import type { AssetGroupRef, AssetSummary, FieldDefinition } from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { useRemoveGroupMembers } from '../../api/hooks/groups';
import { toast } from '../../components/Toasts';
import { AddToGroupDialog, useCanManageGroups } from './AddToGroup';

/**
 * The refs worth showing: a group that is already on the path of another of the asset's groups is left out (an
 * episode in a Season and directly in its Series shows the Season's line once).
 */
export function chainsOf(groups: AssetGroupRef[] | undefined): AssetGroupRef[] {
  const list = groups ?? [];
  return list.filter((g) => !list.some((o) => o !== g && o.path.some((c) => c.id === g.groupId)));
}

/** "Season 1 – Murder House · Episode 3" for one ref, its path as links before it. */
function Chain({ g, links = true }: { g: AssetGroupRef; links?: boolean }) {
  const crumb = (id: string, label: string, key: string) => links
    ? <Link key={key} to={`/groups/${id}`} className="asset-group-link">{label}</Link>
    : <span key={key}>{label}</span>;
  return (
    <span className="asset-group-chain" data-testid="asset-group-chain" data-group-id={g.groupId}>
      {g.path.map((c) => <Fragment key={c.id}>{crumb(c.id, c.title, c.id)}<span className="asset-group-sep" aria-hidden> › </span></Fragment>)}
      {crumb(g.groupId, g.title, g.groupId)}
      {g.number !== null && <span className="asset-group-number"> · {g.memberLabel} {g.number}</span>}
    </span>
  );
}

export function AssetGroupsBreadcrumb({ asset }: { asset: AssetSummary }) {
  const chains = chainsOf(asset.groups);
  if (!chains.length) return null;
  return (
    <nav className="asset-groups-breadcrumb" aria-label="Groups" data-testid="asset-groups-breadcrumb">
      <FolderTree size={13} aria-hidden />
      {chains.map((g, i) => <Fragment key={g.groupId}>{i > 0 && <span className="faint"> · </span>}<Chain g={g} /></Fragment>)}
    </nav>
  );
}

export function AssetGroupsRow({ asset, place }: { asset: AssetSummary; place: 'inspector' | 'metadata' }) {
  const can = useCanManageGroups();
  const remove = useRemoveGroupMembers();
  const [change, setChange] = useState<AssetGroupRef | 'new' | null>(null);
  const groups = asset.groups ?? [];
  if (!groups.length && !can) return null;
  const deleted = Boolean(asset.deleted);

  async function drop(g: AssetGroupRef) {
    if (!window.confirm(`Take ${asset.title} out of ${g.title}? The asset stays in the Library.`)) return;
    try {
      await remove.mutateAsync({ id: g.groupId, assetIds: [asset.id] });
      toast(`Removed from ${g.title}`);
    } catch (err) { toast(errorMessage(err), 'error'); }
  }
  return (
    <div className={`asset-groups-row ${place}`} data-testid="asset-groups-row" data-place={place}>
      <span className="asset-groups-label">Groups</span>
      <div className="asset-groups-list">
        {groups.map((g) => (
          <div className="asset-groups-item" key={g.groupId} data-testid="asset-groups-item">
            <span className="pill">{g.typeLabel}</span>
            <Chain g={g} />
            {can && !deleted && (
              <span className="asset-groups-actions">
                <button className="btn ghost icon sm" aria-label={`Change the ${g.typeLabel.toLowerCase()}`} title={`Move to another ${g.typeLabel.toLowerCase()}`} onClick={() => setChange(g)} data-testid="asset-group-change"><Pencil size={12} /></button>
                <button className="btn ghost icon sm" aria-label={`Remove from ${g.title}`} title={`Remove from ${g.title}`} disabled={remove.isPending} onClick={() => void drop(g)} data-testid="asset-group-remove"><X size={12} /></button>
              </span>
            )}
          </div>
        ))}
        {!groups.length && <span className="faint">In no group</span>}
        {can && !deleted && <button className="btn ghost sm asset-groups-add" onClick={() => setChange('new')} data-testid="asset-group-add"><FolderTree size={12} /> Add to group…</button>}
      </div>
      {change && (
        <AddToGroupDialog assets={[asset]} typeId={change === 'new' ? undefined : change.typeId}
          title={change === 'new' ? `Add ${asset.title} to a group` : `Move ${asset.title} to another ${change.typeLabel.toLowerCase()}`}
          onClose={() => setChange(null)} />
      )}
    </div>
  );
}

/** A value as the grey line shows it. */
export function inheritedText(field: FieldDefinition, v: unknown): string {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.map((x) => inheritedText({ ...field, type: field.type === 'tags' ? 'text' : field.type }, x)).join(', ');
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (field.type === 'vocabulary') return field.options?.find((o) => o.value === v)?.label ?? String(v);
  if (field.type === 'date' && typeof v === 'string') return new Date(`${v}T00:00:00`).toLocaleDateString();
  if (field.type === 'datetime' && typeof v === 'string') return new Date(v).toLocaleString();
  if (field.type === 'rich_text' && typeof v === 'string') return v.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return String(v);
}

const isEmpty = (v: unknown) => v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length);

export function InheritedFieldValue({ asset, field, value }: { asset: AssetSummary; field: FieldDefinition; value: unknown }) {
  const inherited = asset.inheritedMetadata?.[field.id];
  if (!inherited || !isEmpty(value) || isEmpty(inherited.value)) return null;
  const text = inheritedText(field, inherited.value);
  return (
    <div className="inherited-value" data-testid="inherited-value" data-field={field.id} title="Shown because this asset's own field is empty. Typing a value makes it the asset's own; the group's value is never written into the asset">
      <span className="inherited-text">{text}</span>
      <span className="inherited-from">from <Link to={`/groups/${inherited.groupId}`}>{inherited.typeLabel}</Link></span>
    </div>
  );
}
